// device-presence.service.ts
import { Injectable, OnDestroy } from "@angular/core";
import { getAuth } from "firebase/auth";
import {
  getDatabase,
  ref,
  set,
  update,
  onDisconnect,
  onValue,
  serverTimestamp,
  remove,
} from "firebase/database";
import {
  getFirestore,
  doc,
  updateDoc,
  serverTimestamp as firestoreServerTimestamp,
} from "@firebase/firestore";

type SessionState = {
  hb?: ReturnType<typeof setInterval>;
  infoUnsub?: () => void;
  token: string; // guards stale ticks
  started: boolean;
};

const HEARTBEAT_MS = 15_000;

@Injectable({ providedIn: "root" })
export class DevicePresenceService implements OnDestroy {
  private sessions = new Map<string, SessionState>();

  private newToken() {
    return `${Date.now()}_${Math.random().toString(36).slice(2)}`;
  }

  async start(deviceId: string) {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    // Fully stop any prior runtime session (won’t write if not started)
    await this.stop(deviceId, { deleteMapping: false });

    const db = getDatabase();
    const byUserRef = ref(db, `/status/${uid}/connectedDevices/${deviceId}`);
    const byDeviceRef = ref(db, `/devices/${deviceId}/currentUsers/${uid}`);
    const infoRef = ref(db, ".info/connected");

    const token = this.newToken();

    // Arm onDisconnect FIRST (race-safe)
    try {
      await onDisconnect(byUserRef).remove();
    } catch {}
    try {
      await onDisconnect(byDeviceRef).remove();
    } catch {}

    // Mark connected (creates nodes)
    const payload = {
      state: "connected",
      since: serverTimestamp(),
      lastSeen: serverTimestamp(),
    };
    await set(byUserRef, payload).catch(() => {});
    await set(byDeviceRef, payload).catch(() => {});

    // Heartbeat — guarded by token so stale intervals do nothing
    const hb = setInterval(() => {
      const s = this.sessions.get(deviceId);
      if (!s || s.token !== token) return; // stale/removed → no-op
      const tick = { lastSeen: serverTimestamp() };
      update(byUserRef, tick).catch(() => {});
      update(byDeviceRef, tick).catch(() => {});
    }, HEARTBEAT_MS);

    // Re-arm on reconnect
    const infoUnsub = onValue(infoRef, async (snap) => {
      const s = this.sessions.get(deviceId);
      if (!s || s.token !== token) return;
      if (snap.val() === true) {
        try {
          await onDisconnect(byUserRef).remove();
        } catch {}
        try {
          await onDisconnect(byDeviceRef).remove();
        } catch {}
        const bump = { state: "connected", lastSeen: serverTimestamp() };
        update(byUserRef, bump).catch(() => {});
        update(byDeviceRef, bump).catch(() => {});
      }
    });

    this.sessions.set(deviceId, { hb, infoUnsub, token, started: true });
  }

  /** Call on Disconnect button AND on BLE disconnect callback */
  async stop(deviceId: string, opts: { deleteMapping?: boolean } = {}) {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const s = this.sessions.get(deviceId);
    // Guard: if we never started, do nothing (prevents creating nodes by accident)
    if (!s?.started) return;

    // 1) Kill timers/listeners FIRST so no more ticks can run
    if (s.hb) clearInterval(s.hb);
    s.infoUnsub?.();

    // Invalidate any late ticks
    const deadToken = this.newToken();
    this.sessions.set(deviceId, { token: deadToken, started: false });

    const db = getDatabase();
    const byUserRef = ref(db, `/status/${uid}/connectedDevices/${deviceId}`);
    const byDeviceRef = ref(db, `/devices/${deviceId}/currentUsers/${uid}`);

    // 2) Cancel server-side onDisconnect
    try {
      await onDisconnect(byUserRef).cancel();
    } catch {}
    try {
      await onDisconnect(byDeviceRef).cancel();
    } catch {}

    // 3) Write final state
    if (opts.deleteMapping) {
      // Remove completely (atomic fanout)
      const fanout: Record<string, any> = {};
      fanout[`status/${uid}/connectedDevices/${deviceId}`] = null;
      fanout[`devices/${deviceId}/currentUsers/${uid}`] = null;
      await update(ref(db), fanout).catch(() => {});
    } else {
      // Keep node but mark disconnected
      const off = { state: "disconnected", endedAt: serverTimestamp() };
      await update(byUserRef, off).catch(() => {});
      await update(byDeviceRef, off).catch(() => {});
    }

    // 4) Remove session entry
    this.sessions.delete(deviceId);
  }

  /** Convenience: hard delete a mapping right now */
  async removeMapping(deviceId: string) {
    await this.stop(deviceId, { deleteMapping: true });
  }

  async removeConnectionDevice() {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    const db = getDatabase();
    const firestore = getFirestore();

    const userDevicesRef = ref(db, `/status/${uid}/connectedDevices`);
    const userDevicesFirestoreRef = doc(firestore, "/users/" + uid);

    try {
      //remove(userDevicesRef);
      await onDisconnect(userDevicesRef).remove();
      updateDoc(userDevicesFirestoreRef, {
        connectedDevices: "",
      }).catch(() => {});
    } catch {}
  }

  async stopAll() {
    const ids = [...this.sessions.keys()];
    for (const id of ids) await this.stop(id);
  }

  ngOnDestroy(): void {
    this.sessions.forEach((s) => {
      if (s.hb) clearInterval(s.hb);
      s.infoUnsub?.();
    });
    this.sessions.clear();
  }
}
